import { Injectable } from '@angular/core';
import { CollectionHelper, ObjectHelper } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  Query,
  QueryColumnSortOrder,
  QueryObjectsModel,
  QueryOutputMode,
  SubQueryKind,
} from '@barthauer/abc/core/data';
import { ParameterContainer } from '@barthauer/abc/queries/parameters/core';
import { ParameterService } from '@barthauer/abc/queries/parameters/values-editor';
import { _AbcTreeNode } from '@barthauer/abc/ui-components';
import { BehaviorSubject } from 'rxjs';
import { ColumnSpacing, Property } from '../viewModels/abc-queries-editor.viewModel';

@Injectable({
  providedIn: 'root',
})
export class ResultsService {
  private _resultTableCache: Object = null;
  private _resultJsonCache: string = '';
  private _resultXmlCache: string = '';

  public queryDocument: BehaviorSubject<QueryObjectsModel> = new BehaviorSubject(new QueryObjectsModel());

  public get resultTableCache(): Object {
    return this._resultTableCache;
  }
  public set resultTableCache(value: Object) {
    this._resultTableCache = value;
  }

  public get resultJsonCache(): string {
    return this._resultJsonCache;
  }
  public set resultJsonCache(value: string) {
    this._resultJsonCache = value;
  }

  public get resultXmlCache(): string {
    return this._resultXmlCache;
  }
  public set resultXmlCache(value: string) {
    this._resultXmlCache = value;
  }

  constructor(private coreDataService: CoreDataService, private valuesEditorService: ParameterService) {}

  public async createObjectQueries(
    treeData: _AbcTreeNode<Property>,
    queryDocument: ParameterContainer
  ): Promise<Query[]> {
    const objectQueries: Query[] = [];

    const createBody = (node: _AbcTreeNode<Property>): Query => {
      const query: Query = new Query();
      node.data.queryProperties.subQueryKind !== null && node.data.queryProperties.subQueryKind !== SubQueryKind.Sub
        ? (query.DefaultIfEmpty = node.data.queryProperties.subQueryKind === SubQueryKind.LeftJoin ? true : false)
        : (query.Name = node.caption);
      query.IsDistinct = node.data.queryProperties.isDuplicated;
      query.OPath = node.data.expression;
      query.Columns = [];
      query.ObjectType = node.data.name;
      node.data.queryProperties.limitRowsNbr && (query.LimitCount = node.data.queryProperties.limitRowsNbr);
      node.data.queryProperties.skippedRowsNbr && (query.LimitOffset = node.data.queryProperties.skippedRowsNbr);
      node.data.queryProperties.isDuplicated && (query.IsDistinct = node.data.queryProperties.isDuplicated);

      node.children.map((node: _AbcTreeNode<Property>) => {
        if (node.data.columnProperties) {
          query.Columns.push(
            this.coreDataService.createQueryColumn(
              node.data.expression,
              node.caption,
              node.data.columnProperties.sorting || null,
              node.data.columnProperties.filter || null,
              !node.data.columnProperties.visible || null,
              node.data.columnProperties.culture || null,
              node.data.columnProperties.formatExpression?.length ? node.data.columnProperties.formatExpression : null
            )
          );
        } else if (node.data.queryProperties) {
          const subQuery = createBody(node);
          if (!query.ObjectQueries) {
            query.ObjectQueries = [];
          }
          query.ObjectQueries.push(subQuery);
        }
      });
      return query;
    };

    treeData.children.map((node: _AbcTreeNode<Property>) => {
      const resultQuery = createBody(node);
      objectQueries.push(resultQuery);
    });

    this.queryDocument.value.Indent = true;
    this.queryDocument.value.ObjectQueries = objectQueries;
    this.queryDocument.value.ParameterDefinitions = queryDocument?.ParameterDefinitions;
    this.queryDocument.value.Parameters = queryDocument?.Parameters;
    this.queryDocument.next(this.queryDocument.value);

    return objectQueries;
  }

  public createXmlObjectQueries(
    treeData: _AbcTreeNode<Property>,
    objectSpaceName: string,
    rootElementName: string
  ): string {
    let xmlResult = `<ObjectQueryDocument objectSpace="${objectSpaceName}"${
      rootElementName?.length ? `${` rootName="` + rootElementName + `"`}` : ''
    }>\n  <ObjectQueries>\n `;

    const createBody = (tree: _AbcTreeNode<Property>, indentations: number): void => {
      const indentation = ' '.repeat((indentations + 2) * 2);

      xmlResult +=
        `${indentation}<ObjectQuery ` +
        `objectType="${tree.data.name}" ` +
        `${
          tree.data.queryProperties.subQueryKind !== null && tree.data.queryProperties.subQueryKind !== SubQueryKind.Sub
            ? `defaultIfEmpty="${tree.data.queryProperties.subQueryKind === SubQueryKind.LeftJoin ? 'true' : 'false'}"`
            : `${tree.caption ? ` xpath="${tree.caption}"` : ''}`
        }` +
        `${tree.data.expression ? ` opath="${tree.data.expression}"` : ''}` +
        `${tree.data.queryProperties.isDuplicated ? ` distinct="${tree.data.queryProperties.isDuplicated}"` : ''}` +
        `${
          tree.data.queryProperties.skippedRowsNbr ? ` limitOffset="${tree.data.queryProperties.skippedRowsNbr}"` : ''
        }>` +
        `${tree.data.queryProperties.limitRowsNbr ? ` limitCount="${tree.data.queryProperties.limitRowsNbr}"` : ''}\n`;

      const objectQueries = tree.children.filter((node: _AbcTreeNode<Property>) => node.data.queryProperties);

      if (objectQueries.length > 0) {
        xmlResult += `${indentation}  <ObjectQueries>\n`;

        objectQueries.forEach((node: _AbcTreeNode<Property>) => {
          createBody(node, indentations + 2);
        });

        xmlResult += `${indentation}  </ObjectQueries>\n`;
      } else {
        xmlResult += `${indentation}  <ObjectQueries />\n`;
      }

      const columnsIndentation = ' '.repeat((indentations + 4) * 2);
      const columns = tree.children.filter((node: _AbcTreeNode<Property>) => node.data.columnProperties);

      if (columns && columns.length > 0) {
        xmlResult += `${indentation}  <Columns>\n`;

        columns.forEach((node: _AbcTreeNode<Property>) => {
          xmlResult +=
            `${columnsIndentation}<Column ` +
            `opath="${node.data.expression}" ` +
            `xpath="` +
            `${node.caption}"` +
            `${node.data.columnProperties.filter ? ` filter="true"` : ''}` +
            `${
              node.data.columnProperties.spacing !== ColumnSpacing.IGNOR_BLANK
                ? ` optional="${
                    node.data.columnProperties.spacing === ColumnSpacing.IGNOR_BLANK_PROPERTIES ? 'true' : 'false'
                  }"`
                : ''
            }` +
            `${
              node.data.columnProperties.sorting !== QueryColumnSortOrder.None
                ? ` order="${
                    node.data.columnProperties.sorting === QueryColumnSortOrder.Ascending ? 'Ascending' : 'Descending'
                  }"`
                : ''
            }` +
            `${
              node.data.columnProperties.formatExpression?.length
                ? ` format="${node.data.columnProperties.formatExpression}"`
                : ''
            }` +
            `${node.data.columnProperties.culture ? ` invariant="true"` : ''}` +
            `${!node.data.columnProperties.visible ? ` hidden="true"` : ''}>\n`;
        });

        xmlResult += `${indentation}  </Columns>\n`;
      } else {
        xmlResult += `${indentation}  <Columns />\n`;
      }
      xmlResult += `${indentation}</ObjectQuery>\n`;
    };

    treeData.children.map((tree: _AbcTreeNode<Property>) => {
      createBody(tree, 0);
    });

    xmlResult += '  </ObjectQueries>\n</ObjectQueryDocument>\n';

    return xmlResult;
  }

  public executeQueries = async (queryObjectModel: QueryObjectsModel): Promise<ArrayBuffer | string> => {
    const parameterNamesToExclude =
      queryObjectModel.ParameterDefinitions?.filter((parameter) => parameter.IsFixed).map(
        (parameter) => parameter.Name
      ) ?? [];

    const parameterValues =
      (await this.valuesEditorService.fillParametersIfExist(
        queryObjectModel.ParameterDefinitions,
        parameterNamesToExclude
      )) ?? [];

    if (queryObjectModel.Parameters?.length) {
      for (const p of queryObjectModel.Parameters) {
        if (!parameterValues.find((_) => _.Name === p.Name)) {
          parameterValues.push(p);
        }
      }
    }

    queryObjectModel = ObjectHelper.cloneDeep(queryObjectModel);
    queryObjectModel.Parameters = parameterValues;

    let defaultName: string;
    if (
      queryObjectModel.OutputMode !== QueryOutputMode.Xml &&
      CollectionHelper.distinct(queryObjectModel.ObjectQueries, (_) => _.Name).length === 1
    ) {
      for (const q of queryObjectModel.ObjectQueries) {
        defaultName = q.Name;
        q.Name = null;
      }
    }

    switch (queryObjectModel.OutputMode) {
      case QueryOutputMode.Json:
        let data: any = await this.coreDataService.executeReadObjectsQuery(queryObjectModel).toPromise();
        if (defaultName) {
          const newdata = {};
          if (Array.isArray(data)) {
            newdata[defaultName] = data;
          } else {
            newdata[defaultName] = [];
          }
          data = newdata;
        }
        return data;

      case QueryOutputMode.Xml:
        return await this.coreDataService.executeReadObjectsQueryXML(queryObjectModel).toPromise();
      default:
        throw new Error(`Unsupported OutputMode: ${queryObjectModel.OutputMode}`);
    }
  };
}
